'use client';

import { useState, FormEvent } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Mail, AlertCircle, CheckCircle, Loader2, Zap, ArrowLeft } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { toast } from '@/components/ui/sonner';
import { cn } from '@/lib/utils';
import { useAuth } from '@/providers/auth-provider';

export function ForgotPasswordPage() {
  const { forgotPassword } = useAuth();
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitted, setSubmitted] = useState(false);

  const validateEmail = (value: string) => {
    if (!value) return 'Email is required';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return 'Invalid email format';
    return null;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const emailError = validateEmail(email);
    if (emailError) {
      setErrors({ email: emailError });
      return;
    }
    setErrors({});
    setIsLoading(true);
    try {
      await forgotPassword(email);
      setSubmitted(true);
      toast.success('Check your email', { description: 'Password reset instructions have been sent.' });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to send reset email';
      toast.error('Error', { description: message });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
          <Card className="glass-strong">
            <CardHeader className="text-center">
              <Link to="/" className="inline-flex items-center justify-center mb-6">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-cyan-500 to-purple-500">
                  <Zap className="h-7 w-7 text-white" />
                </div>
              </Link>
              <CardTitle className="text-2xl font-bold text-white">Forgot password?</CardTitle>
              <CardDescription className="text-slate-400">Enter your email and we'll send you a link to reset your password</CardDescription>
            </CardHeader>
            <CardContent>
              {submitted ? (
                <div className="text-center py-8">
                  <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-500/10">
                    <CheckCircle className="h-8 w-8 text-green-400" />
                  </div>
                  <h3 className="text-lg font-medium text-white mb-2">Email sent!</h3>
                  <p className="text-slate-400 mb-6">Check your inbox for password reset instructions. The link expires in 1 hour.</p>
                  <Button variant="outline" asChild>
                    <Link to="/login">
                      <ArrowLeft className="mr-2 h-4 w-4" />
                      Back to login
                    </Link>
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-slate-300">Email address</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-500" />
                      <Input
                        id="email"
                        type="email"
                        placeholder="you@example.com"
                        value={email}
                        onChange={e => setEmail(e.target.value)}
                        className={cn('pl-10', errors.email && 'border-red-500/50 focus:border-red-500 focus:ring-red-500/20')}
                        disabled={isLoading}
                        autoComplete="email"
                        autoFocus
                      />
                    </div>
                    {errors.email && <p className="text-sm text-red-400 flex items-center gap-1"><AlertCircle className="h-3 w-3" />{errors.email}</p>}
                  </div>

                  <Button type="submit" className="w-full py-3" disabled={isLoading} size="lg">
                    {isLoading ? <> <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sending... </> : 'Send reset link'}
                  </Button>
                </form>
              )}
            </CardContent>
            <CardFooter className="flex flex-col gap-4">
              <Separator className="w-full" />
              <p className="text-center text-sm text-slate-400">
                Remember your password? <Link to="/login" className="text-cyan-400 hover:text-cyan-300 font-medium transition-colors">Sign in</Link>
              </p>
            </CardFooter>
          </Card>

          <p className="text-center text-xs text-slate-500 mt-6">
            By continuing, you agree to our{' '}
            <Link to="/terms" className="text-cyan-400 hover:underline">Terms of Service</Link>{' '}
            and{' '}
            <Link to="/privacy" className="text-cyan-400 hover:underline">Privacy Policy</Link>
          </p>
        </motion.div>
      </div>
    );
}